// src/oura/livePageWorkshop.ts
//
// Wire → workshop adapter for the Pages Workshop (epic ourteam-backend#4104).
//
// The BE ships snake_case (`block_id`, `anchor_text`, `matched_text`,
// `role_source`) because that is what the handoff specified and what it built
// against; this feature module speaks camelCase. Rather than bend either side,
// the translation lives here — the same seam `liveAudit.ts` occupies for the
// assessment payload.
//
// Two rules run through everything below:
//
//   1. Never invent. A field the BE did not send stays undefined; it does not
//      get a plausible default. A fabricated `matched_text` is a wrong sentence
//      highlighted on a client's live page.
//   2. Never widen. Values that are a closed set on our side (`related` labels,
//      roles, block tags) are checked, not cast — an unexpected value falls back
//      to something safe and visible rather than crashing the render or, worse,
//      rendering as if it were valid.

import type {
    AeoLinkGraph,
    AeoPageContent,
    AeoPageDraft,
    AeoPageHistoryEntry,
    AeoVoiceProfile,
} from "@service/aeo";
import { catchErrorLog } from "@util/sentry";
import {
    isKnownPageHistoryKind,
    linkBudgetForRole,
    roleForUrl,
    type BlockTag,
    type PageBlock,
    type PageHistoryChange,
    type PageHistoryEntry,
    type PageRecommendation,
    type PageRole,
    type PageSearchMetrics,
    type PageWorkshop,
    type RelatedLabel,
} from "./pageWorkshop";

const BLOCK_TAGS: BlockTag[] = ["h1", "h2", "h3", "p", "li", "img", "table", "stat", "quote"];
const PAGE_ROLE_VALUES: PageRole[] = [
    "home",
    "guide",
    "blog post",
    "comparison",
    "conversion",
    "content page",
];
const RELATED_LABELS: RelatedLabel[] = ["Related reading", "Compare", "Regional guides"];

/** An unknown tag renders as a paragraph rather than blanking the block. The
 *  BE folds h4–h6 to h3 already, so this is a genuine contract surprise — the
 *  reviewer still needs to read the text. */
function toBlockTag(tag: string): BlockTag {
    return (BLOCK_TAGS as string[]).includes(tag) ? (tag as BlockTag) : "p";
}

/** The BE's own fallback for an out-of-set related label, mirrored here so a
 *  surprise value cannot reach a closed union. */
function toRelatedLabel(label: string): RelatedLabel {
    return (RELATED_LABELS as string[]).includes(label)
        ? (label as RelatedLabel)
        : "Related reading";
}

/**
 * `role` is a closed set on both sides, but the BE may add one before we do.
 * An unrecognised role falls back to the FE's own URL inference rather than to
 * a hard-coded "content page" — a guess from the URL is at least derived from
 * something, and it is the rule the BE itself starts from.
 */
export function toPageRole(role: string | undefined, url: string): PageRole {
    if (role && (PAGE_ROLE_VALUES as string[]).includes(role)) return role as PageRole;
    return roleForUrl(url);
}

function adaptBlock(b: AeoPageContent["blocks"][number]): PageBlock {
    return {
        id: b.id,
        tag: toBlockTag(b.tag),
        text: b.text ?? "",
        // Every one of these is optional on the wire and must stay optional
        // here: `alt: ""` on a block with no alt would render an empty caption
        // that looks like a missing one.
        ...(b.alt !== undefined ? { alt: b.alt } : {}),
        ...(b.src !== undefined ? { src: b.src } : {}),
        ...(b.figure !== undefined ? { figure: b.figure } : {}),
        ...(b.label !== undefined ? { label: b.label } : {}),
        ...(b.table !== undefined ? { table: b.table } : {}),
        ...(b.group !== undefined ? { group: b.group } : {}),
        ...(b.links !== undefined
            ? { links: b.links.map((l) => ({ text: l.text, href: l.href })) }
            : {}),
    };
}

/**
 * One proposal, wire → workshop.
 *
 * Returns null for a kind we do not model. Dropping one proposal is a smaller
 * failure than rendering a malformed one: the reviewer sees fewer suggestions,
 * not a wrong suggestion presented as reviewed.
 */
function adaptRecommendation(
    r: AeoPageDraft["recommendations"][number],
): PageRecommendation | null {
    const justification = {
        reason: r.justification?.reason ?? "",
        expected: r.justification?.expected ?? "",
    };
    switch (r.kind) {
        case "copy":
            return {
                kind: "copy",
                id: r.id,
                blockId: r.block_id,
                before: r.before,
                after: r.after,
                justification,
            };
        case "link":
            return {
                kind: "link",
                id: r.id,
                blockId: r.block_id,
                anchorText: r.anchor_text,
                targetUrl: r.target_url,
                targetTitle: r.target_title,
                requiresNewCopy: r.requires_new_copy === true,
                // `null` on the wire means "no sentence needed"; it must not
                // become the string "null" or an empty proposed sentence.
                ...(r.proposed_copy ? { proposedCopy: r.proposed_copy } : {}),
                ...(r.match ? { match: r.match } : {}),
                ...(r.matched_text ? { matchedText: r.matched_text } : {}),
                ...(r.pairs_with
                    ? { pairsWith: { url: r.pairs_with.url, title: r.pairs_with.title } }
                    : {}),
                justification,
            };
        case "repoint":
            return {
                kind: "repoint",
                id: r.id,
                blockId: r.block_id,
                anchorText: r.anchor_text,
                currentUrl: r.current_url,
                targetUrl: r.target_url,
                targetTitle: r.target_title,
                justification,
            };
        case "related":
            return {
                kind: "related",
                id: r.id,
                blockId: r.block_id,
                label: toRelatedLabel(r.label),
                links: (r.links ?? []).map((l) => ({ url: l.url, title: l.title })),
                justification,
            };
        default:
            return null;
    }
}

function adaptHistoryChange(c: AeoPageHistoryEntry["changes"][number]): PageHistoryChange {
    return {
        kind: c.kind,
        where: c.where,
        ...(c.before !== undefined ? { before: c.before } : {}),
        ...(c.after !== undefined ? { after: c.after } : {}),
        ...(c.anchor_text !== undefined ? { anchorText: c.anchor_text } : {}),
        ...(c.target_url !== undefined ? { targetUrl: c.target_url } : {}),
        ...(c.from_url !== undefined ? { fromUrl: c.from_url } : {}),
    };
}

function adaptHistoryEntry(e: AeoPageHistoryEntry): PageHistoryEntry {
    // The wire's `kind` is an open string; the FE union is hand-written and
    // nothing validated it, so a new BE event type reached render and crashed
    // the page (#3513). Report it ONCE here, at the seam, rather than letting
    // it surface as a rendering oddity nobody can trace back to a payload.
    if (e.kind && !isKnownPageHistoryKind(e.kind)) {
        catchErrorLog(`[oura] unknown page history kind from the BE: "${e.kind}" — add it to HISTORY_KIND`, "warning");
    }
    return {
        id: e.id,
        kind: e.kind,
        at: e.at,
        by: e.by,
        changes: (e.changes ?? []).map(adaptHistoryChange),
        ...(e.via ? { via: e.via } : {}),
        ...(e.instruction ? { instruction: e.instruction } : {}),
    };
}

/**
 * Assemble one page's workshop from the pieces the BE returns separately.
 *
 * `content` is the only required input — a page with no draft yet is a real and
 * common state (nobody has asked Oura for proposals), and it must render as the
 * extracted document with an empty proposal list, not as an error.
 */
export function adaptPageWorkshop(input: {
    content: AeoPageContent;
    draft: AeoPageDraft | null;
    history: AeoPageHistoryEntry[];
    metrics: PageSearchMetrics | null;
    voice: AeoVoiceProfile | null;
}): PageWorkshop {
    const { content, draft, history, metrics, voice } = input;
    const role = toPageRole(content.role, content.url);
    return {
        // Straight from the page read; no defaulting. A backend without the
        // field means we do not know, which is different from "writable".
        publishable: input.content.publishable ?? null,
        url: content.url,
        title: content.title || content.url,
        // The id the publish call writes from. Only present once a draft exists;
        // a page that has only been read has nothing to publish.
        draftId: draft?.draft_id,
        role,
        // Prefer the BE's number so the notice and the generator agree. Falls
        // back to our own rule for a payload that predates `recommended_links`.
        linkBudget: content.recommended_links ?? linkBudgetForRole(role),
        blocks: (content.blocks ?? []).map(adaptBlock),
        recommendations: (draft?.recommendations ?? [])
            .map(adaptRecommendation)
            .filter((r): r is PageRecommendation => r !== null),
        history: (history ?? []).map(adaptHistoryEntry),
        metrics,
        // A null voice is deliberate on the BE side — it returns nothing rather
        // than "friendly and professional", which describes every business
        // alive and would make voice-matching unfalsifiable. Mirrored as an
        // empty summary so the UI can say "not derived" instead of inventing.
        voice: {
            summary: voice?.summary ?? "",
            sampledFrom: voice?.sampled_from ?? [],
        },
    };
}

/**
 * Whether the link graph is usable enough to trust link proposals.
 *
 * Not cosmetic. Every `link` and `repoint` proposal is derived from the graph,
 * so an empty one means the suggestions on screen were computed from nothing.
 * Distinguishing the two absences matters: a 404 says the graph was never
 * built, while zero edges says the crawl genuinely found no links — and those
 * lead to different conversations.
 */
export function linkGraphHealth(graph: AeoLinkGraph | null): {
    state: "absent" | "empty" | "nav_only" | "ok";
    edges: number;
    inBodyEdges: number;
} {
    if (!graph) return { state: "absent", edges: 0, inBodyEdges: 0 };
    const edges = graph.edges?.length ?? 0;
    const inBodyEdges = (graph.edges ?? []).filter((e) => e.in_body).length;
    if (edges === 0) return { state: "empty", edges, inBodyEdges };
    if (inBodyEdges === 0) return { state: "nav_only", edges, inBodyEdges };
    return { state: "ok", edges, inBodyEdges };
}
